<script lang="ts">
	import { string, object } from 'yup'
	import { DOCUMENTS, PRIVACY_POLICY } from '$lib/routes'
	import { getNewsletterPopupState } from '$lib/stores'
	import { t } from '$lib/translations'
	import { setCookie } from '$lib/utils'
	import { PUBLIC_WOLTAIR_WEB_CODE } from '$env/static/public'

	const newsletterPopupState = getNewsletterPopupState()

	let formStep: 'DEFAULT' | 'SENDING' | 'SENT' = 'DEFAULT'

	const schema = object({
		email: string().email(t('web.forms.email.validate')).required(t('web.forms.email.required'))
	})

	let formErrors: Record<string, any> = {}
	let formData: Record<string, any> = {
		email: ''
	}

	async function validateField(fieldName: string, value: string) {
		try {
			await schema.validateAt(fieldName, { [fieldName]: value })
			formErrors[fieldName] = ''
		} catch (error: any) {
			formErrors[fieldName] = error.message
		}
	}

	async function handleSubmit() {
		try {
			formErrors = {}
			await Promise.all(
				Object.entries(formData).map(async ([key, value]) => {
					await validateField(key, value.toString() || '')
				})
			)
			if (Object.values(formErrors).some((error) => error)) throw new Error('Validation failed')

			formStep = 'SENDING'

			const response = await fetch('/api/lead/create', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					...formData,
					...((PUBLIC_WOLTAIR_WEB_CODE === 'WOLTAIR_CZ' ||
						PUBLIC_WOLTAIR_WEB_CODE === 'WOLTAIR_DE') && { marketingConsentGeneral: true }),
					leadSource: 'NEWSLETTER_POPUP'
				})
			})

			if (response.ok) {
				formStep = 'SENT'
				setTimeout(() => {
					newsletterPopupState.update((state) => ({
						...state,
						open: false
					}))
					setCookie('NWSLTPP', 'false', 30)
				}, 2000)
			} else {
				formStep = 'DEFAULT'
			}
		} catch (error: any) {
			if (error.inner) {
				if (error.inner.length > 0)
					error.inner.forEach((err: any) => {
						formErrors[err.path] = err.message
					})
			}
		}
	}
</script>

<div
	class="fixed inset-x-0 bottom-20 z-[999] mx-auto flex justify-center px-2 3xs:bottom-16 md:bottom-8"
>
	<div
		class="{formStep === 'SENT'
			? 'fadeOutDown'
			: 'fadeInUp'} relative w-full max-w-[500px] overflow-hidden rounded-xl bg-w-blue-950 px-4 py-4 shadow-2xl md:mx-auto md:rounded-2xl md:p-5 md:pt-4"
	>
		<div class="flex items-start justify-between gap-3">
			<h4 class="unstyled h4 mb-0 mt-0 text-white">{@html t('web.modals.newsletter.title')}</h4>
			<button
				on:click={() => {
					newsletterPopupState.update((state) => ({
						...state,
						open: false
					}))
					setCookie('NWSLTPP', 'false', 1)
				}}
				><img
					src="/images/icons/close-x.svg"
					alt="Close icon"
					class="w-3 transition-opacity hover:opacity-80"
				/></button
			>
		</div>
		<p class="unstyled mt-2 text-xs leading-normal text-white opacity-70">
			<span class="hidden md:inline">{@html t('web.modals.newsletter.text')}</span>
			<span class="md:hidden">{@html t('web.modals.newsletter.text.mobile')}</span>
		</p>
		<form on:submit|preventDefault={handleSubmit} class="mt-4 flex w-full gap-2">
			<input
				type="text"
				class="w-full rounded-md text-xs placeholder:opacity-50 md:text-sm"
				placeholder={t('web.forms.email.placeholder.example')}
				class:border-w-red-500={formErrors.email}
				bind:value={formData.email}
				on:change={() => validateField('email', formData.email)}
				disabled={formStep === 'SENDING' || formStep === 'SENT'}
				class:opacity-70={formStep === 'SENDING' || formStep === 'SENT'}
			/>
			<button
				class="btn-new btn-new-red point rounded-md text-xs"
				type="submit"
				class:pointer-events-none={formStep === 'SENDING' || formStep === 'SENT'}
				>{#if formStep === 'SENDING'}
					<img
						src="/images/icons/loader-2.svg"
						class="w-[1.25em] animate-spin"
						alt="Loader icon"
					/>{@html t('web.forms.submit.sending')}
				{:else if formStep === 'SENT'}
					<img src="/images/icons/tick.svg" class="w-[1.25em]" alt="Loader icon" />
					{@html t('web.forms.submit.sent')}
				{:else}
					<span class="hidden md:inline">{@html t('web.modals.newsletter.button')}</span>
					<span class="md:hidden">{@html t('web.modals.newsletter.button.mobile')}</span>
				{/if}</button
			>
		</form>
		{#if formErrors.email}
			<p class="unstyled text-xs text-w-red-500">{formErrors.email}</p>{/if}
		<p class="unstyled mt-3 text-[10px] font-light text-white opacity-70">
			{@html t('web.forms.conditions', {
				link: `/${DOCUMENTS}/${PRIVACY_POLICY}`,
				class: 'underline'
			})}
		</p>
	</div>
</div>

<style lang="scss">
	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translate3d(0, 100%, 0);
		}

		to {
			opacity: 1;
			transform: translate3d(0, 0, 0);
		}
	}

	.fadeInUp {
		transform: translate3d(0, 100%, 0);
		animation-name: fadeInUp;
		animation-duration: 0.3s;
		animation-fill-mode: forwards;
	}

	@keyframes fadeOutDown {
		from {
			opacity: 1;
			transform: translate3d(0, 0, 0);
		}
		to {
			opacity: 0;
			transform: translate3d(0, 100%, 0);
		}
	}

	.fadeOutDown {
		animation-name: fadeOutDown;
		animation-duration: 0.3s;
		animation-fill-mode: forwards;
		animation-delay: 1s;
	}
</style>
